import { CategorySchema } from "@/actions/schemas/categories";
import { z } from "zod";
import { StoreIdSchema } from "@/actions/schemas/stores";

enum Status {
  Draft = "draft",
  Archived = "archived",
  Public = "public",
}

enum StatusLabel {
  Draft = "Rascunho",
  Archived = "Arquivado",
  Public = "Público",
}

enum OrderBy {
  Category = "category",
  CreatedAt = "createdAt",
  Name = "name",
  SalePrice = "salePrice",
  Status = "status",
  Subcategory = "subcategory",
}

enum OrderDirection {
  Asc = "asc",
  Desc = "desc",
}

type Product = z.infer<typeof ProductSchema>;

type ProductImage = z.infer<typeof ImagesSchema>;

type GetProductInput = z.infer<typeof GetProductInputSchema>;

type GetProductOutput = z.infer<typeof GetProductOutputSchema>;

type GetProductListInput = z.infer<typeof GetProductListInputSchema>;

type GetProductListOutput = z.infer<typeof GetProductListOutputSchema>;

const ImageSchema = z.object({
  id: z.string(),
  url: z.string(),
});

const TaxSchema = z.object({
  ncm: z.string().nullable(),
  cfop: z.string().nullable(),
  cest: z.string().nullable(),
  csosn: z.string().nullable(),
  icmsAliquot: z.number().nullable(),
  icmsStAliquot: z.number().nullable(),
  icmsAliquotDif: z.number().nullable(),
  icmsReduction: z.number().nullable(),
  icmsStReduction: z.number().nullable(),
  icmsBenefitCode: z.string().nullable(),
  icmsLegalProtection: z.string().nullable(),
  cstIpi: z.string().nullable(),
  ipiAliquot: z.number().nullable(),
  ipiFramingCode: z.string().nullable(),
  ipiLegalProtection: z.string().nullable(),
  cstPis: z.string().nullable(),
  pisAliquot: z.number().nullable(),
  cstCofins: z.string().nullable(),
  cofinsAliquot: z.number().nullable(),
});

const ImagesSchema = z
  .array(ImageSchema)
  .nullable()
  .transform((value) => value ?? []);

const VariantSchema = z.object({
  id: z.string(),
  gtin: z.string().nullable(),
  name: z.string(),
  price: z.object({
    sale: z
      .number()
      .nullable()
      .transform((value) => value ?? 0),
  }),
  sku: z.string().nullable(),
  stock: z
    .object({
      current: z
        .number()
        .nullable()
        .transform((value) => value ?? 0),
    })
    .nullable(),
  chosenVariationOptions: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        option: z.object({
          id: z.string(),
          name: z.string(),
        }),
      })
    )
    .nullable()
    .transform((value) => value ?? []),
  images: ImagesSchema,
  tax: TaxSchema.nullable(),
});

const ProductSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: CategorySchema.nullable(),
  subcategory: CategorySchema.nullable(),
  description: z.string().nullable(),
  images: ImagesSchema,
  status: z.enum(Status),
  variants: z
    .array(VariantSchema)
    .nullable()
    .transform((value) => value ?? []),
  variationsTypes: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        existentVariationOptions: z.array(
          z.object({
            id: z.string(),
            name: z.string(),
          })
        ),
      })
    )
    .nullable(),
});

const GetProductInputSchema = z.object({
  storeId: StoreIdSchema,
  productId: z.string().min(1),
});

const GetProductOutputSchema = ProductSchema;

const GetProductListInputSchema = z.object({
  categoryId: z.string().nullish(),
  cursor: z.string().nullish(),
  pageSize: z.number().nullish(),
  productName: z.string().nullish(),
  status: z.enum(Status),
  storeId: StoreIdSchema,
  orderBy: z.enum(OrderBy).nullish(),
  orderDirection: z.enum(OrderDirection).nullish(),
  withStock: z.boolean().nullish(),
});

const GetProductListOutputSchema = z.object({
  items: z
    .array(ProductSchema)
    .nullable()
    .transform((value) => value ?? []),
  nextPageCursor: z.string().nullable(),
});

export {
  ProductSchema,
  GetProductInputSchema,
  GetProductOutputSchema,
  GetProductListInputSchema,
  GetProductListOutputSchema,
  Status,
  StatusLabel,
  OrderBy,
  OrderDirection,
  type Product,
  type ProductImage,
  type GetProductInput,
  type GetProductOutput,
  type GetProductListInput,
  type GetProductListOutput,
};
